import { useState, useEffect } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart3, 
  Calendar, 
  Download, 
  FileText, 
  Filter, 
  Leaf, 
  TrendingUp,
  Droplets,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Tractor
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BilansPage() {
  const { activeSociete } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [interventions, setInterventions] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  const [campagne, setCampagne] = useState('2026');
  const [stats, setStats] = useState({
    total: 0,
    terminees: 0,
    enCours: 0,
    planifiees: 0,
    parType: {}
  });

  useEffect(() => {
    fetchData();
  }, [activeSociete, campagne]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        Authorization: `Bearer ${token}`,
        'X-Societe-Id': activeSociete?.id || ''
      };

      const [intervRes, parcRes] = await Promise.all([
        axios.get(`${API}/api/parcellaire/interventions?campagne=${campagne}`, { headers }),
        axios.get(`${API}/api/parcellaire/parcelles?campagne=${campagne}`, { headers })
      ]);

      const intervs = intervRes.data.interventions || [];
      const parcs = parcRes.data.parcelles || [];
      
      setInterventions(intervs);
      setParcelles(parcs);

      // Calculer les stats
      const terminees = intervs.filter(i => i.statut === 'termine').length;
      const enCours = intervs.filter(i => i.statut === 'en_cours').length;
      const planifiees = intervs.filter(i => i.statut === 'planifie').length;
      
      // Stats par type
      const parType = {};
      intervs.forEach(i => {
        const type = i.type_intervention || 'autre';
        if (!parType[type]) parType[type] = { count: 0, terminees: 0 };
        parType[type].count++;
        if (i.statut === 'termine') parType[type].terminees++;
      });

      setStats({
        total: intervs.length,
        terminees,
        enCours,
        planifiees,
        parType
      });

    } catch (error) {
      console.error('Erreur chargement bilans:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      travail_sol: 'Travail du sol',
      semis: 'Semis',
      traitement: 'Traitement phyto',
      fertilisation: 'Fertilisation',
      recolte: 'Récolte',
      irrigation: 'Irrigation',
      autre: 'Autre'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'traitement': return <Droplets className="w-4 h-4" />;
      case 'semis': return <Leaf className="w-4 h-4" />;
      case 'recolte': return <TrendingUp className="w-4 h-4" />;
      case 'travail_sol': return <Tractor className="w-4 h-4" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (statut) => {
    switch(statut) {
      case 'termine':
        return <Badge variant="default" className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Terminé</Badge>;
      case 'en_cours':
        return <Badge variant="secondary" className="bg-blue-500 text-white"><Clock className="w-3 h-3 mr-1" />En cours</Badge>;
      case 'planifie':
        return <Badge variant="outline"><Calendar className="w-3 h-3 mr-1" />Planifié</Badge>;
      case 'annule':
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Annulé</Badge>;
      default:
        return <Badge variant="outline">{statut}</Badge>;
    }
  };

  // Bilans par parcelle
  const bilansParcelles = parcelles.map(p => {
    const intervsParcelle = interventions.filter(i => i.parcelle_id === p.id);
    return {
      ...p,
      interventions: intervsParcelle,
      nbInterventions: intervsParcelle.length,
      terminees: intervsParcelle.filter(i => i.statut === 'termine').length
    };
  }).sort((a, b) => b.nbInterventions - a.nbInterventions);

  return (
    <div className="space-y-6" data-testid="bilans-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-primary" />
            Bilans d'Intervention
          </h1>
          <p className="text-muted-foreground">
            Synthèse des interventions - Campagne {campagne}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={campagne} onValueChange={setCampagne}>
            <SelectTrigger className="w-32" data-testid="campagne-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" data-testid="export-bilans-btn">
            <Download className="w-4 h-4 mr-2" />
            Exporter
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="stat-total">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total interventions</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <Calendar className="w-10 h-10 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-terminees">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Terminées</p>
                <p className="text-3xl font-bold text-green-600">{stats.terminees}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-encours">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">En cours</p>
                <p className="text-3xl font-bold text-blue-600">{stats.enCours}</p>
              </div>
              <Clock className="w-10 h-10 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-planifiees">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Planifiées</p>
                <p className="text-3xl font-bold text-amber-600">{stats.planifiees}</p>
              </div>
              <Calendar className="w-10 h-10 text-amber-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="synthese" className="space-y-4">
        <TabsList>
          <TabsTrigger value="synthese" data-testid="tab-synthese">Synthèse</TabsTrigger>
          <TabsTrigger value="par-parcelle" data-testid="tab-parcelle">Par parcelle</TabsTrigger>
          <TabsTrigger value="par-type" data-testid="tab-type">Par type</TabsTrigger>
        </TabsList>

        {/* Synthèse */}
        <TabsContent value="synthese" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Répartition par type d'intervention</CardTitle>
              <CardDescription>Vue d'ensemble des interventions réalisées</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-40 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : Object.keys(stats.parType).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Aucune intervention pour cette campagne</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(stats.parType).map(([type, data]) => (
                    <div key={type} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        {getTypeIcon(type)}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{getTypeLabel(type)}</p>
                        <p className="text-sm text-muted-foreground">
                          {data.terminees}/{data.count} terminées
                        </p>
                      </div>
                      <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${(data.terminees / data.count) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-12 text-right">
                        {Math.round((data.terminees / data.count) * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Par Parcelle */}
        <TabsContent value="par-parcelle" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Interventions par parcelle</CardTitle>
              <CardDescription>Détail des interventions sur chaque parcelle</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-40 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : bilansParcelles.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Leaf className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Aucune parcelle trouvée</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {bilansParcelles.map(p => (
                    <div key={p.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div 
                        className="w-3 h-10 rounded-full"
                        style={{ backgroundColor: p.culture_couleur || '#9ca3af' }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.nom}</p>
                        <p className="text-sm text-muted-foreground">
                          {p.culture_nom || 'Non affecté'} • {p.surface_ha} ha
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{p.nbInterventions} intervention{p.nbInterventions > 1 ? 's' : ''}</p>
                        <p className="text-sm text-muted-foreground">
                          {p.terminees} terminée{p.terminees > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Par Type */}
        <TabsContent value="par-type" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historique par type</CardTitle>
              <CardDescription>Liste détaillée des interventions</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-40 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : interventions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Aucune intervention enregistrée</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {interventions.map(interv => (
                    <div key={interv.id} className="flex items-center gap-4 p-3 border rounded-lg">
                      <div className="p-2 bg-muted rounded-lg">
                        {getTypeIcon(interv.type_intervention)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{getTypeLabel(interv.type_intervention)}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {interv.parcelle_nom} • {new Date(interv.date_debut).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      {getStatusBadge(interv.statut)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
